import React, { useState } from 'react';
// AidaCMSTemplate.tsx
// CMS-ready template for AIDA Framework content pages
// Drop this into any React/Next.js CMS (Contentful, Sanity, Notion-backed, etc.)

// ─── CMS DATA TYPES ──────────────────────────────────────────────────────────
export interface AidaStage {
  id: 'attention' | 'interest' | 'desire' | 'action';
  number: string;
  letter: string;
  name: string;
  tagline: string;
  body: string;
  keyQuestion: string;
  tone: string;
  engagementPct: number;
}
export interface AidaExample {
  label: string;
  attention: string;
  interest: string;
  desire: string;
  action: string;
}
export interface CrossDomainExample {
  domain: string;
  description: string;
}
export interface AudienceFitRow {
  audience: string;
  fit: 'excellent' | 'good' | 'strong' | 'weak';
  description: string;
}
export interface AidaPageData {
  // Hero
  heroTitle: string;
  heroSubtitle: string;
  authorName: string;
  year: string;
  domain: string;
  heroQuote: string;
  stats: {
    label: string;
    value: string;
  }[];
  // Core Idea
  coreIdeaBody: string[];
  historicalContext: string;
  centralTension: string;
  lewisQuote: string;
  // Stages
  stages: AidaStage[];
  // Examples
  classicExample: AidaExample & {
    title: string;
  };
  modernExample: AidaExample & {
    title: string;
  };
  crossDomainExamples: CrossDomainExample[];
  counterExample: {
    title: string;
    body: string;
  };
  // Practical Guide
  practicalSteps: {
    title: string;
    body: string;
  }[];
  templateLines: {
    label: string;
    placeholder: string;
  }[];
  workedExample: {
    stage: string;
    text: string;
  }[];
  // Pitfalls
  pitfalls: {
    title: string;
    body: string;
  }[];
  // Fit & Limits
  bestUseCases: {
    label: string;
    body: string;
  }[];
  whereItStruggles: string;
  audienceFitMatrix: AudienceFitRow[];
  // Science
  psychologicalBasis: string;
  culturalUniversality: string;
  // Takeaway
  takeawaySteps: string;
  takeawayExample: string;
  theOneRule: string;
}
// ─── DEFAULT CMS CONTENT ─────────────────────────────────────────────────────
export const defaultAidaData: AidaPageData = {
  heroTitle: 'AIDA',
  heroSubtitle: 'Attention · Interest · Desire · Action',
  authorName: 'Elias St. Elmo Lewis',
  year: '1898',
  domain: 'Advertising & Marketing',
  heroQuote: 'The four-step ladder every buyer climbs before they open their wallet — and the reason most persuasion fails.',
  stats: [{
    label: 'Complexity',
    value: 'Simple'
  }, {
    label: 'Best for',
    value: 'Copywriters'
  }, {
    label: 'Stages',
    value: '4'
  }, {
    label: 'Age',
    value: '127 yrs'
  }],
  coreIdeaBody: ["Most persuasion fails not because the message is wrong, but because it arrives at the wrong moment in the buyer's mind. Lewis observed that a person doesn't decide to buy all at once — they pass through four distinct mental states, each requiring a different kind of communication. Skip a state, and you lose them. Arrive at the right state with the wrong message, and you lose them. AIDA is a map of that journey.", "The framework's central tension is between awareness and motivation. Awareness is passive — you can force it with a loud headline. Motivation is earned — it requires that you first make the reader feel the gap between where they are and where they could be. AIDA structures the path from passive noticing to active wanting."],
  lewisQuote: "Lewis noticed that the best salespeople he observed weren't using pressure — they were sequencing. First they made you look, then made you lean in, then made you want, then made you move.",
  historicalContext: 'Elias St. Elmo Lewis was an American advertising pioneer who formulated this model in 1898, first as a theory of salesmanship, later as a framework for print advertising. His original formulation appeared in trade publications as a description of what great salespeople naturally did. By the 1920s it had been adopted as a foundational model in advertising education and has remained the dominant entry-level persuasion framework ever since — surviving a century of disruption because its insight is psychological, not technological.',
  centralTension: "The framework navigates the gap between noticing and acting. Every failed piece of communication collapses at one of four points: it doesn't break through (no Attention), it doesn't reward attention (no Interest), it doesn't create felt need (no Desire), or it doesn't make the next step obvious (no Action). AIDA is a diagnostic for exactly which of these four failures occurred.",
  stages: [{
    id: 'attention',
    number: '01',
    letter: 'A',
    name: 'Attention',
    tagline: 'Stop the scroll. Interrupt the pattern.',
    body: "Attention is not about being loud — it's about being relevant at the moment of first contact. In a world of scroll, swipe, and skip, you have 0.5–2 seconds to earn continued reading. The job of Attention is not to sell; it's to earn the next sentence.",
    keyQuestion: 'Why should I stop here?',
    tone: 'Disruptive / Unexpected',
    engagementPct: 30
  }, {
    id: 'interest',
    number: '02',
    letter: 'I',
    name: 'Interest',
    tagline: 'Reward the pause. Make them lean in.',
    body: "You've stopped them — now justify the stop. Interest converts a momentary pause into sustained engagement. This is where specificity beats volume: one precise insight about your reader's world is worth more than three paragraphs of general claims.",
    keyQuestion: 'Is this relevant to me?',
    tone: 'Engaging / Specific',
    engagementPct: 55
  }, {
    id: 'desire',
    number: '03',
    letter: 'D',
    name: 'Desire',
    tagline: 'Create felt need. Bridge gap to self.',
    body: 'This is where most frameworks stop — and where AIDA goes further. Desire is not about listing features. It is about creating emotional ownership: helping the reader vividly imagine themselves after the transformation. The job of desire is to make the gap between now and the promised state feel unbearable.',
    keyQuestion: 'Do I want this?',
    tone: 'Aspirational / Urgent',
    engagementPct: 88
  }, {
    id: 'action',
    number: '04',
    letter: 'A',
    name: 'Action',
    tagline: 'Make the next step obvious.',
    body: 'Desire without direction evaporates. The action stage removes every reason not to act right now: friction, ambiguity, risk. A strong call to action is specific (exactly what to do), singular (one thing only), and low-stakes in framing (removes perceived cost or risk).',
    keyQuestion: 'What do I do now?',
    tone: 'Clear / Urgent',
    engagementPct: 72
  }],
  classicExample: {
    title: "Classic example — Apple's 2001 iPod launch",
    label: 'Apple iPod',
    attention: 'A full-page ad: stark white, one device, no text except the headline — "1,000 songs in your pocket." In an era of clunky MP3 players, this stopped readers cold.',
    interest: 'The body copy explained the breakthrough: your entire CD collection, compressed, instantly searchable with a single scroll wheel. The technology became personally imaginable.',
    desire: "Early ads showed silhouettes dancing freely — no cords, no cases, no friction. The imagery didn't sell features; it sold a feeling of liberation that was impossible to un-imagine once seen.",
    action: 'Single URL. Single price. Available at the Apple Store. No configuration, no bundles — just one obvious next step that removed every reason to hesitate.'
  },
  modernExample: {
    title: "Modern example — Duolingo's onboarding flow",
    label: 'Duolingo Onboarding',
    attention: "The app opens with an immediate micro-lesson — no account creation, no forms. You're already doing the thing before you've decided to commit.",
    interest: "Progress is shown in real-time: a progress bar fills, XP appears, a streak counter starts. The system makes learning feel like a game you're already winning.",
    desire: 'At the end of the first lesson, you\'re asked: "How committed are you?" and shown streaks of other learners. Social proof + personal goal-setting ignites ownership.',
    action: "Sign up is deferred until you've already invested effort — sunk cost makes the CTA feel like saving progress, not making a commitment."
  },
  crossDomainExamples: [{
    domain: 'Film trailers',
    description: 'Attention: an explosion in the first 2 seconds. Interest: a mystery question posed mid-trailer. Desire: the protagonist\'s face in a moment of triumph. Action: release date + "In cinemas now."'
  }, {
    domain: 'LinkedIn cold outreach',
    description: 'Attention via a personalised subject referencing recent company news. Interest via a single specific pain point in their industry. Desire via a concrete outcome ("saved 3 hours/week"). Action via one low-friction ask: "Worth a 15-minute call?"'
  }, {
    domain: 'Charity appeals',
    description: 'Attention: a single child\'s face (not a statistic). Interest: their specific story. Desire: "Your £3 can buy them this." Action: a pre-filled donation form with suggested amount.'
  }],
  counterExample: {
    title: 'Counter-example — Google Glass (2013)',
    body: 'Google Glass launched with a powerful Attention moment — a Sergey Brin skydive demo — but collapsed at Desire. It never answered: what specific problem does this solve for you? The gap between the spectacle and the felt need was never bridged. AIDA predicted the failure precisely: dazzling Attention, weak Interest (unclear relevance), and almost no Desire — users were fascinated but not wanting.'
  },
  practicalSteps: [{
    title: 'Write your attention hook first, separately',
    body: 'Draft 5 different opening lines — a statistic, a question, a bold claim, a counter-intuitive fact, a direct address. Test each against: would this stop a scrolling thumb? Keep only the one that would.'
  }, {
    title: 'Build interest with specificity, not volume',
    body: 'Write one paragraph that answers: "Why should someone exactly like my reader care about this?" Name their role, their specific frustration, or their existing goal. Generic = friction. Specific = forward momentum.'
  }, {
    title: 'Manufacture desire through before/after contrast',
    body: "Write two sentences: one describing the reader's current painful reality; one describing their life after the transformation. Make both concrete. The gap between those two sentences is where desire lives."
  }, {
    title: 'Write one CTA — and remove everything else',
    body: 'End with exactly one instruction. Not "share, subscribe, or buy." One. Make it a verb: "Start free trial." "Book a call." "Download now." Then add a single risk-reversal (free, no card required, cancel anytime).'
  }],
  templateLines: [{
    label: 'Attention',
    placeholder: '[Surprising statistic / bold claim / question that stops your reader]'
  }, {
    label: 'Interest',
    placeholder: '[1–2 sentences: why this is specifically relevant to [reader type] who [faces specific situation]]'
  }, {
    label: 'Desire',
    placeholder: '[Paint the before: currently you [painful reality]. Imagine [vivid after-state with sensory detail].]'
  }, {
    label: 'Action',
    placeholder: '[Single verb CTA] + [one risk-reversal phrase]'
  }],
  workedExample: [{
    stage: 'A',
    text: '"Your team spent 11 hours last month on status updates."'
  }, {
    stage: 'I',
    text: '"For SaaS teams under 20 people, async reporting eats more time than the actual work it reports on."'
  }, {
    stage: 'D',
    text: '"Right now, you\'re writing it manually. With Briefcase, your team\'s weekly update writes itself — and lands in Slack before your Monday standup."'
  }, {
    stage: 'A',
    text: '"Start your free 14-day trial. No card required."'
  }],
  pitfalls: [{
    title: 'Leading with features, not a hook.',
    body: '"We offer 14 integrations and a 99.9% uptime SLA" is Interest-level content delivered where Attention should be. It assumes the reader is already engaged.'
  }, {
    title: 'Skipping Desire entirely.',
    body: 'Going from product explanation straight to a CTA is the most common AIDA failure. The reader knows what you do but has not been made to want it.'
  }, {
    title: 'Multiple competing CTAs.',
    body: '"Buy now, or share this post, or sign up for our newsletter" is three Actions that produce zero. Every choice you add reduces the chance of any action happening.'
  }, {
    title: 'Using AIDA as a rigid checklist.',
    body: 'The stages are psychological states, not paragraphs. A single well-crafted headline can carry both Attention and Interest simultaneously.'
  }],
  bestUseCases: [{
    label: 'Email marketing',
    body: 'The sequential format of email naturally mirrors the funnel: subject line (A), preview text and opener (I), body (D), button (A).'
  }, {
    label: 'Sales pages & landing pages',
    body: "Long-form persuasion contexts where you control the scroll — AIDA ensures each section earns the reader's continued attention."
  }, {
    label: 'Pitch decks and investor narratives',
    body: 'AIDA maps cleanly onto a deck structure: market insight (A), problem specificity (I), product-market fit vision (D), ask (A).'
  }],
  whereItStruggles: 'AIDA assumes a cold audience encountering your message for the first time. For warm audiences (existing customers, subscribers, referrals), leading with Attention tricks can feel manipulative or condescending — they already trust you. It also struggles with deeply considered purchases (B2B enterprise, medical decisions) where the Desire stage requires months of relationship-building that no single piece of content can achieve.',
  audienceFitMatrix: [{
    audience: 'General / Consumer',
    fit: 'excellent',
    description: "Excellent fit — high-volume, cold audiences respond well to AIDA's sequential logic"
  }, {
    audience: 'Professional / B2B',
    fit: 'good',
    description: 'Good fit for cold outreach; weaker for multi-stakeholder decisions needing deeper trust'
  }, {
    audience: 'Emotional / Cause',
    fit: 'strong',
    description: 'Strong — especially for charity, advocacy, and social campaigns where emotion drives action'
  }, {
    audience: 'Technical / Expert',
    fit: 'weak',
    description: 'Weak fit — experts resent Attention tricks; lead with Interest (relevance) from line one'
  }],
  psychologicalBasis: "AIDA maps onto the brain's decision-making architecture. Attention exploits the orienting response — the brainstem's automatic redirect toward novelty or threat. Interest works through elaborative encoding: we remember what we connect to existing knowledge and self-concept. Desire activates the mesolimbic dopamine system — anticipatory reward circuitry fires hardest not when you receive something, but when you vividly imagine receiving it. Action addresses the intention-behavior gap: research in behavioral economics shows that vague intentions rarely convert; a concrete, frictionless action removes the cognitive cost of deciding how to proceed.",
  culturalUniversality: "Aristotle's Rhetoric (330 BCE) described persuasion as requiring attention (ethos-establishing), sustained engagement (logos), and emotional movement (pathos) — a structural parallel that predates Lewis by two millennia. Rhetorical traditions across Sanskrit, Arabic, and Chinese classical writing all describe analogous sequences. AIDA's survival for 127 years suggests it describes something pre-linguistic about how minds move from passive reception to motivated action.",
  takeawaySteps: 'Stop them → Reward the stop → Make them want → Make the step obvious.',
  takeawayExample: '"11 hours wasted on status updates" → "Here\'s why teams like yours..." → "Imagine it writing itself" → "Start free — no card needed."',
  theOneRule: 'Never reach for the Action stage until the reader has felt the Desire — logic informs, but only felt need converts.'
};
// ─── THEME ───────────────────────────────────────────────────────────────────
const theme = {
  attention: {
    bg: '#EEEDFE',
    border: '#AFA9EC',
    label: '#534AB7',
    heading: '#26215C',
    body: '#3C3489',
    pill: {
      bg: '#EEEDFE',
      text: '#3C3489',
      border: '#AFA9EC'
    }
  },
  interest: {
    bg: '#E1F5EE',
    border: '#5DCAA5',
    label: '#0F6E56',
    heading: '#04342C',
    body: '#085041',
    pill: {
      bg: '#E1F5EE',
      text: '#085041',
      border: '#5DCAA5'
    }
  },
  desire: {
    bg: '#FAEEDA',
    border: '#EF9F27',
    label: '#854F0B',
    heading: '#412402',
    body: '#633806',
    pill: {
      bg: '#FAEEDA',
      text: '#633806',
      border: '#EF9F27'
    }
  },
  action: {
    bg: '#FCEBEB',
    border: '#F09595',
    label: '#A32D2D',
    heading: '#501313',
    body: '#791F1F',
    pill: {
      bg: '#FCEBEB',
      text: '#791F1F',
      border: '#F09595'
    }
  }
};
type StageId = keyof typeof theme;
const stageTheme = (id: string) => theme[id as StageId] ?? theme.attention;
// ─── SUB-COMPONENTS ──────────────────────────────────────────────────────────
const SectionTag: React.FC<{
  children: React.ReactNode;
}> = ({
  children
}) => <p style={{
  fontSize: 10,
  fontWeight: 500,
  textTransform: 'uppercase',
  letterSpacing: '1.2px',
  color: '#5f5e5a',
  marginBottom: 6
}} data-id="element-56">
    {children}
  </p>;
const SectionTitle: React.FC<{
  children: React.ReactNode;
}> = ({
  children
}) => <h2 style={{
  fontFamily: "'DM Serif Display', Georgia, serif",
  fontSize: 26,
  color: '#1a1a18',
  marginBottom: 14,
  fontWeight: 400
}} data-id="element-57">
    {children}
  </h2>;
const SubTitle: React.FC<{
  children: React.ReactNode;
  style?: React.CSSProperties;
}> = ({
  children,
  style
}) => <p style={{
  fontSize: 14,
  fontWeight: 500,
  color: '#1a1a18',
  margin: '20px 0 8px',
  ...style
}} data-id="element-58">
    {children}
  </p>;
const BodyText: React.FC<{
  children: React.ReactNode;
  style?: React.CSSProperties;
}> = ({
  children,
  style
}) => <p style={{
  fontSize: 15,
  color: '#5f5e5a',
  lineHeight: 1.75,
  marginBottom: 12,
  ...style
}} data-id="element-59">
    {children}
  </p>;
const Divider = () => <hr style={{
  height: 0,
  border: 'none',
  borderTop: '0.5px solid rgba(0,0,0,0.12)',
  margin: '2.5rem 0'
}} data-id="element-60" />;
const QuoteBlock: React.FC<{
  text: string;
}> = ({
  text
}) => <blockquote style={{
  borderLeft: '3px solid rgba(0,0,0,0.22)',
  padding: '10px 18px',
  margin: '16px 0'
}} data-id="element-61">
    <p style={{
    fontSize: 15,
    fontStyle: 'italic',
    color: '#5f5e5a',
    lineHeight: 1.7
  }} data-id="element-62">
      {text}
    </p>
  </blockquote>;
const ExampleCard: React.FC<{
  label: string;
  text: string;
  labelColor?: string;
}> = ({
  label,
  text,
  labelColor = '#5f5e5a'
}) => <div style={{
  background: '#f7f6f3',
  borderRadius: 8,
  padding: '14px 16px',
  marginBottom: 8
}} data-id="element-63">
    <p style={{
    fontSize: 10,
    fontWeight: 500,
    textTransform: 'uppercase',
    letterSpacing: '.6px',
    marginBottom: 4,
    color: labelColor
  }} data-id="element-64">
      {label}
    </p>
    <p style={{
    fontSize: 14,
    color: '#1a1a18',
    lineHeight: 1.65
  }} data-id="element-65">
      {text}
    </p>
  </div>;
// ─── STAGE CARD ───────────────────────────────────────────────────────────────
const StageCard: React.FC<{
  stage: AidaStage;
}> = ({
  stage
}) => {
  const t = stageTheme(stage.id);
  return <div style={{
    borderRadius: 12,
    padding: '18px 20px',
    border: `0.5px solid ${t.border}`,
    background: t.bg,
    marginBottom: 10
  }} data-id="element-66">
      <p style={{
      fontSize: 10,
      fontWeight: 500,
      textTransform: 'uppercase',
      letterSpacing: '.6px',
      color: t.label,
      marginBottom: 4
    }} data-id="element-67">
        Stage {stage.number}
      </p>
      <p style={{
      fontSize: 17,
      fontWeight: 500,
      color: t.heading,
      marginBottom: 8
    }} data-id="element-68">
        {stage.name}
      </p>
      <p style={{
      fontSize: 13,
      lineHeight: 1.65,
      color: t.body,
      marginBottom: 12
    }} data-id="element-69">
        {stage.body}
      </p>
      <div style={{
      display: 'flex',
      gap: 8,
      flexWrap: 'wrap' as const
    }} data-id="element-70">
        {[`Key question: ${stage.keyQuestion}`, `Tone: ${stage.tone}`].map(label => <span key={label} style={{
        fontSize: 11,
        padding: '3px 10px',
        borderRadius: 12,
        border: `0.5px solid ${t.pill.border}`,
        background: t.pill.bg,
        color: t.pill.text
      }} data-id="element-71">
              {label}
            </span>)}
      </div>
    </div>;
};
// ─── ENGAGEMENT BAR ───────────────────────────────────────────────────────────
const EngagementBars: React.FC<{
  stages: AidaStage[];
}> = ({
  stages
}) => <div style={{
  marginTop: 28
}} data-id="element-72">
    <p style={{
    fontSize: 13,
    fontWeight: 500,
    color: '#5f5e5a',
    marginBottom: 12
  }} data-id="element-73">
      Emotional arc — how the reader feels
    </p>
    <div style={{
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 10
  }} data-id="element-74">
      {stages.map(s => {
      const t = stageTheme(s.id);
      return <div key={s.id} style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12
      }} data-id="element-75">
            <span style={{
          width: 80,
          fontSize: 12,
          fontWeight: 500,
          color: t.label,
          flexShrink: 0
        }} data-id="element-76">
              {s.name}
            </span>
            <div style={{
          flex: 1,
          background: '#f0ede8',
          borderRadius: 4,
          height: 8,
          overflow: 'hidden'
        }} data-id="element-77">
              <div style={{
            width: `${s.engagementPct}%`,
            height: '100%',
            background: t.label,
            borderRadius: 4,
            transition: 'width 0.6s ease'
          }} data-id="element-78" />
            </div>
            <span style={{
          width: 36,
          fontSize: 11,
          color: '#5f5e5a',
          textAlign: 'right' as const
        }} data-id="element-79">
              {s.engagementPct}%
            </span>
          </div>;
    })}
    </div>
  </div>;
// ─── FUNNEL SVG ───────────────────────────────────────────────────────────────
const AidaFunnel: React.FC<{
  stages: AidaStage[];
}> = ({
  stages
}) => {
  const colors = stages.map(s => stageTheme(s.id));
  const rows = [{
    x: 40,
    width: 480,
    y: 12
  }, {
    x: 90,
    width: 380,
    y: 70
  }, {
    x: 140,
    width: 280,
    y: 124
  }, {
    x: 190,
    width: 180,
    y: 178
  }];
  return <svg viewBox="0 0 560 240" xmlns="http://www.w3.org/2000/svg" style={{
    width: '100%',
    marginBottom: 20
  }} aria-label="AIDA funnel" data-id="element-80">
      {stages.map((s, i) => {
      const r = rows[i];
      const c = colors[i];
      return <g key={s.id} data-id="element-81">
            <rect x={r.x} y={r.y} width={r.width} height={48} rx={8} fill={c.bg} stroke={c.border} strokeWidth={0.5} data-id="element-82" />
            <text x={280} y={r.y + 19} textAnchor="middle" fontSize={11} fill={c.body} fontWeight={500} fontFamily="DM Sans, sans-serif" data-id="element-83">
              {s.letter} — {s.name.toUpperCase()}
            </text>
            <text x={280} y={r.y + 37} textAnchor="middle" fontSize={10} fill={c.label} fontFamily="DM Sans, sans-serif" data-id="element-84">
              {s.tagline}
            </text>
          </g>;
    })}
    </svg>;
};
// ─── STEP ROW ─────────────────────────────────────────────────────────────────
const StepRow: React.FC<{
  step: {
    title: string;
    body: string;
  };
  index: number;
  stages: AidaStage[];
}> = ({
  step,
  index,
  stages
}) => {
  const t = stageTheme(stages[index]?.id ?? 'attention');
  return <div style={{
    display: 'flex',
    gap: 14,
    alignItems: 'flex-start',
    padding: 14,
    background: '#f7f6f3',
    borderRadius: 8,
    marginBottom: 8
  }} data-id="element-85">
      <div style={{
      width: 28,
      height: 28,
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: 12,
      fontWeight: 500,
      flexShrink: 0,
      marginTop: 2,
      background: t.bg,
      color: t.body,
      border: `0.5px solid ${t.border}`
    }} data-id="element-86">
        {index + 1}
      </div>
      <div data-id="element-87">
        <p style={{
        fontSize: 13,
        fontWeight: 500,
        color: '#1a1a18',
        marginBottom: 4
      }} data-id="element-88">
          {step.title}
        </p>
        <p style={{
        fontSize: 13,
        color: '#5f5e5a',
        lineHeight: 1.65
      }} data-id="element-89">
          {step.body}
        </p>
      </div>
    </div>;
};
// ─── AUDIENCE FIT BADGE ───────────────────────────────────────────────────────
const fitConfig: Record<string, {
  bg: string;
  border: string;
  label: string;
  text: string;
}> = {
  excellent: {
    bg: '#E1F5EE',
    border: '#5DCAA5',
    label: '#085041',
    text: '#0F6E56'
  },
  good: {
    bg: '#FAEEDA',
    border: '#EF9F27',
    label: '#633806',
    text: '#854F0B'
  },
  strong: {
    bg: '#EEEDFE',
    border: '#AFA9EC',
    label: '#3C3489',
    text: '#534AB7'
  },
  weak: {
    bg: '#f7f6f3',
    border: 'rgba(0,0,0,0.12)',
    label: '#1a1a18',
    text: '#5f5e5a'
  }
};
// ─── MAIN TEMPLATE ────────────────────────────────────────────────────────────
export interface AidaTemplateProps {
  data?: AidaPageData;
}
const AidaCMSTemplate: React.FC<AidaTemplateProps> = ({
  data = defaultAidaData
}) => {
  const [activeTab, setActiveTab] = useState<'classic' | 'modern'>('classic');
  const stageLetterColors: Record<string, string> = {
    A: theme.attention.label,
    I: theme.interest.label,
    D: theme.desire.label
  };
  // second A (action) needs special handling
  const workedLabelColor = (stage: string, index: number) => {
    if (stage === 'A' && index === 0) return theme.attention.label;
    if (stage === 'A' && index === 3) return theme.action.label;
    return stageLetterColors[stage] ?? '#534AB7';
  };
  return <div style={{
    fontFamily: "'DM Sans', system-ui, sans-serif",
    background: '#ffffff',
    color: '#1a1a18',
    lineHeight: 1.7,
    padding: '2rem 1rem'
  }} data-id="element-90">
      {/* Google Fonts */}
      <style data-id="element-91">{`@import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,400;0,9..40,500;1,9..40,400&family=DM+Serif+Display&display=swap');`}</style>

      <div style={{
      maxWidth: 740,
      margin: '0 auto'
    }} data-id="element-92">
        {/* ── HERO ── */}
        <header style={{
        padding: '2.5rem 0 2rem',
        borderBottom: '0.5px solid rgba(0,0,0,0.12)'
      }} data-id="element-93">
          <h1 style={{
          fontFamily: "'DM Serif Display', Georgia, serif",
          fontSize: 52,
          lineHeight: 1.05,
          marginBottom: 6
        }} data-id="element-94">
            {data.heroTitle}
          </h1>
          <p style={{
          fontSize: 16,
          color: '#5f5e5a',
          marginBottom: 20
        }} data-id="element-95">
            {data.heroSubtitle}
          </p>
          <div style={{
          display: 'flex',
          flexWrap: 'wrap' as const,
          gap: 8,
          marginBottom: 22
        }} data-id="element-96">
            {[data.authorName, data.year, data.domain].map((pill, i) => {
            const colors = [{
              bg: '#EEEDFE',
              text: '#3C3489',
              border: '#AFA9EC'
            }, {
              bg: '#E1F5EE',
              text: '#085041',
              border: '#5DCAA5'
            }, {
              bg: '#FAEEDA',
              text: '#633806',
              border: '#EF9F27'
            }];
            const c = colors[i];
            return <span key={pill} style={{
              display: 'inline-flex',
              alignItems: 'center',
              padding: '5px 14px',
              borderRadius: 20,
              fontSize: 12,
              fontWeight: 500,
              border: `0.5px solid ${c.border}`,
              background: c.bg,
              color: c.text
            }} data-id="element-97">
                  {pill}
                </span>;
          })}
          </div>
          <QuoteBlock text={data.heroQuote} data-id="element-98" />
          <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, minmax(0,1fr))',
          gap: 10,
          marginTop: 22
        }} data-id="element-99">
            {data.stats.map(s => <div key={s.label} style={{
            background: '#f7f6f3',
            borderRadius: 8,
            padding: 14,
            textAlign: 'center' as const
          }} data-id="element-100">
                <p style={{
              fontSize: 10,
              textTransform: 'uppercase' as const,
              letterSpacing: '.6px',
              color: '#5f5e5a',
              marginBottom: 6
            }} data-id="element-101">
                  {s.label}
                </p>
                <p style={{
              fontSize: 17,
              fontWeight: 500
            }} data-id="element-102">
                  {s.value}
                </p>
              </div>)}
          </div>
        </header>

        {/* ── CORE IDEA ── */}
        <section style={{
        margin: '2.5rem 0'
      }} data-id="element-103">
          <SectionTag data-id="element-104">The core idea</SectionTag>
          <SectionTitle data-id="element-105">What it really is</SectionTitle>
          {data.coreIdeaBody.map((para, i) => <BodyText key={i} data-id="element-106">{para}</BodyText>)}
          <QuoteBlock text={data.lewisQuote} data-id="element-107" />
          <SubTitle data-id="element-108">Historical context</SubTitle>
          <BodyText data-id="element-109">{data.historicalContext}</BodyText>
          <SubTitle data-id="element-110">The central tension</SubTitle>
          <BodyText data-id="element-111">{data.centralTension}</BodyText>
        </section>

        <Divider data-id="element-112" />

        {/* ── STRUCTURE ── */}
        <section style={{
        margin: '2.5rem 0'
      }} data-id="element-113">
          <SectionTag data-id="element-114">Structure visualisation</SectionTag>
          <SectionTitle data-id="element-115">The shape of the story</SectionTitle>
          <AidaFunnel stages={data.stages} data-id="element-116" />
          {data.stages.map(stage => <StageCard key={stage.id} stage={stage} data-id="element-117" />)}
          <EngagementBars stages={data.stages} data-id="element-118" />
        </section>

        <Divider data-id="element-119" />

        {/* ── EXAMPLES ── */}
        <section style={{
        margin: '2.5rem 0'
      }} data-id="element-120">
          <SectionTag data-id="element-121">Real-world examples</SectionTag>
          <SectionTitle data-id="element-122">Seeing it in action</SectionTitle>

          {/* Tab switcher */}
          <div style={{
          display: 'flex',
          gap: 8,
          marginBottom: 16
        }} data-id="element-123">
            {(['classic', 'modern'] as const).map(tab => <button key={tab} onClick={() => setActiveTab(tab)} style={{
            padding: '6px 16px',
            borderRadius: 20,
            fontSize: 12,
            fontWeight: 500,
            border: `0.5px solid ${activeTab === tab ? '#AFA9EC' : 'rgba(0,0,0,0.12)'}`,
            background: activeTab === tab ? '#EEEDFE' : 'transparent',
            color: activeTab === tab ? '#3C3489' : '#5f5e5a',
            cursor: 'pointer'
          }} data-id="element-124">
                {tab === 'classic' ? 'Classic — Apple iPod' : 'Modern — Duolingo'}
              </button>)}
          </div>

          {(() => {
          const ex = activeTab === 'classic' ? data.classicExample : data.modernExample;
          return <div data-id="element-125">
                <SubTitle style={{
              marginTop: 0
            }} data-id="element-126">
                  {ex.title}
                </SubTitle>
                <ExampleCard label="Attention" text={ex.attention} labelColor={theme.attention.label} data-id="element-127" />
                <ExampleCard label="Interest" text={ex.interest} labelColor={theme.interest.label} data-id="element-128" />
                <ExampleCard label="Desire" text={ex.desire} labelColor={theme.desire.label} data-id="element-129" />
                <ExampleCard label="Action" text={ex.action} labelColor={theme.action.label} data-id="element-130" />
              </div>;
        })()}

          <SubTitle style={{
          marginTop: 24
        }} data-id="element-131">
            Cross-domain examples
          </SubTitle>
          {data.crossDomainExamples.map(ex => <ExampleCard key={ex.domain} label={ex.domain} text={ex.description} data-id="element-132" />)}

          <SubTitle style={{
          marginTop: 24
        }} data-id="element-133">
            Counter-example — {data.counterExample.title}
          </SubTitle>
          <div style={{
          background: '#f7f6f3',
          borderRadius: 8,
          padding: '14px 16px',
          border: '0.5px solid rgba(0,0,0,0.22)'
        }} data-id="element-134">
            <p style={{
            fontSize: 14,
            color: '#1a1a18',
            lineHeight: 1.65
          }} data-id="element-135">
              {data.counterExample.body}
            </p>
          </div>
        </section>

        <Divider data-id="element-136" />

        {/* ── PRACTICAL GUIDE ── */}
        <section style={{
        margin: '2.5rem 0'
      }} data-id="element-137">
          <SectionTag data-id="element-138">How to apply it</SectionTag>
          <SectionTitle data-id="element-139">The practical guide</SectionTitle>
          {data.practicalSteps.map((step, i) => <StepRow key={i} step={step} index={i} stages={data.stages} data-id="element-140" />)}

          <SubTitle data-id="element-141">Fill-in template</SubTitle>
          <div style={{
          background: '#f7f6f3',
          borderRadius: 12,
          padding: '18px 20px',
          border: '0.5px solid rgba(0,0,0,0.12)',
          fontSize: 13,
          lineHeight: 2.1,
          color: '#5f5e5a'
        }} data-id="element-142">
            {data.templateLines.map((line, i) => {
            const stageId = data.stages[i]?.id ?? 'attention';
            const t = stageTheme(stageId);
            return <div key={line.label} data-id="element-143">
                  <strong style={{
                color: t.label
              }} data-id="element-144">
                    [{line.label}]
                  </strong>{' '}
                  — {line.placeholder}
                  {i < data.templateLines.length - 1 && <br data-id="element-145" />}
                </div>;
          })}
          </div>

          <SubTitle data-id="element-146">Worked example — SaaS email</SubTitle>
          <div style={{
          background: '#f7f6f3',
          borderRadius: 12,
          padding: '18px 20px',
          border: '0.5px solid rgba(0,0,0,0.12)',
          fontSize: 13,
          lineHeight: 2.1
        }} data-id="element-147">
            {data.workedExample.map((line, i) => <div key={i} data-id="element-148">
                <strong style={{
              color: workedLabelColor(line.stage, i)
            }} data-id="element-149">
                  [{line.stage}]
                </strong>{' '}
                <span style={{
              color: '#5f5e5a'
            }} data-id="element-150">
                  {line.text}
                </span>
                {i < data.workedExample.length - 1 && <br data-id="element-151" />}
              </div>)}
          </div>

          <SubTitle data-id="element-152">Pitfalls to avoid</SubTitle>
          {data.pitfalls.map((p, i) => <div key={i} style={{
          display: 'flex',
          gap: 12,
          padding: '12px 0',
          borderBottom: i < data.pitfalls.length - 1 ? '0.5px solid rgba(0,0,0,0.12)' : 'none',
          alignItems: 'flex-start'
        }} data-id="element-153">
              <div style={{
            width: 22,
            height: 22,
            borderRadius: '50%',
            background: '#FCEBEB',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            marginTop: 3,
            color: '#E24B4A',
            fontSize: 13,
            fontWeight: 700
          }} data-id="element-154">
                ✕
              </div>
              <p style={{
            fontSize: 13,
            color: '#5f5e5a',
            lineHeight: 1.65
          }} data-id="element-155">
                <strong style={{
              color: '#1a1a18'
            }} data-id="element-156">
                  {p.title}
                </strong>{' '}
                {p.body}
              </p>
            </div>)}
        </section>

        <Divider data-id="element-157" />

        {/* ── FIT & LIMITS ── */}
        <section style={{
        margin: '2.5rem 0'
      }} data-id="element-158">
          <SectionTag data-id="element-159">When to use it — and when not to</SectionTag>
          <SectionTitle data-id="element-160">Fit and limits</SectionTitle>

          <SubTitle data-id="element-161">Best use cases</SubTitle>
          {data.bestUseCases.map(uc => <ExampleCard key={uc.label} label={uc.label} text={uc.body} data-id="element-162" />)}

          <SubTitle data-id="element-163">Where it struggles</SubTitle>
          <BodyText data-id="element-164">{data.whereItStruggles}</BodyText>

          <SubTitle data-id="element-165">Audience fit matrix</SubTitle>
          <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 8
        }} data-id="element-166">
            {data.audienceFitMatrix.map(row => {
            const c = fitConfig[row.fit] ?? fitConfig.weak;
            return <div key={row.audience} style={{
              borderRadius: 8,
              padding: 14,
              border: `0.5px solid ${c.border}`,
              background: c.bg
            }} data-id="element-167">
                  <p style={{
                fontSize: 12,
                fontWeight: 500,
                color: c.label,
                marginBottom: 5
              }} data-id="element-168">
                    {row.audience}
                  </p>
                  <p style={{
                fontSize: 11,
                lineHeight: 1.6,
                color: c.text
              }} data-id="element-169">
                    {row.description}
                  </p>
                </div>;
          })}
          </div>
        </section>

        <Divider data-id="element-170" />

        {/* ── SCIENCE ── */}
        <section style={{
        margin: '2.5rem 0'
      }} data-id="element-171">
          <SectionTag data-id="element-172">The science behind it</SectionTag>
          <SectionTitle data-id="element-173">Why it works</SectionTitle>
          <SubTitle data-id="element-174">Psychological basis</SubTitle>
          <BodyText data-id="element-175">{data.psychologicalBasis}</BodyText>
          <SubTitle data-id="element-176">Cultural universality</SubTitle>
          <BodyText data-id="element-177">{data.culturalUniversality}</BodyText>
        </section>

        <Divider data-id="element-178" />

        {/* ── QUICK REFERENCE ── */}
        <section style={{
        margin: '2.5rem 0'
      }} data-id="element-179">
          <SectionTag data-id="element-180">Quick reference card</SectionTag>
          <SectionTitle data-id="element-181">The takeaway</SectionTitle>

          <div style={{
          background: '#f7f6f3',
          borderRadius: 12,
          padding: '22px 24px',
          border: '0.5px solid rgba(0,0,0,0.12)'
        }} data-id="element-182">
            <p style={{
            fontSize: 16,
            fontWeight: 500,
            marginBottom: 16
          }} data-id="element-183">
              AIDA at a glance
            </p>
            <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, minmax(0,1fr))',
            gap: 8,
            marginBottom: 18
          }} data-id="element-184">
              {data.stages.map(s => {
              const t = stageTheme(s.id);
              return <div key={s.id} style={{
                textAlign: 'center' as const,
                padding: '14px 8px',
                background: t.bg,
                borderRadius: 8
              }} data-id="element-185">
                    <p style={{
                  fontFamily: "'DM Serif Display', Georgia, serif",
                  fontSize: 28,
                  color: t.body,
                  marginBottom: 2
                }} data-id="element-186">
                      {s.letter}
                    </p>
                    <p style={{
                  fontSize: 11,
                  color: t.label
                }} data-id="element-187">
                      {s.name}
                    </p>
                  </div>;
            })}
            </div>
            <p style={{
            fontSize: 13,
            color: '#5f5e5a',
            lineHeight: 1.8,
            marginBottom: 4
          }} data-id="element-188">
              {data.takeawaySteps}
            </p>
            <p style={{
            fontSize: 13,
            color: '#5f5e5a',
            lineHeight: 1.8,
            marginBottom: 4
          }} data-id="element-189">
              Example: {data.takeawayExample}
            </p>
            <div style={{
            fontSize: 15,
            fontWeight: 500,
            color: '#1a1a18',
            borderLeft: '3px solid #1D9E75',
            padding: '12px 16px',
            background: '#E1F5EE',
            borderRadius: '0 8px 8px 0',
            marginTop: 16,
            lineHeight: 1.6
          }} data-id="element-190">
              {data.theOneRule}
            </div>
          </div>
        </section>
      </div>
    </div>;
};
export { AidaCMSTemplate as FrameworkEditor };
export default AidaCMSTemplate;